import React, { useEffect, useRef, useState } from "react";
import Layout from "../layout/index";
import axios from "axios";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { ROOT_API } from "../../api/config";
import { formatDate, formatDateTime } from "../../utils/dates";
import { isPremiumUser } from "../../utils/premium";
import "./style.scss";

const CHANNELS = ["email", "sms", "both"];

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("access_token")}`,
});

// Enterprise address photos. Private — each photo is fetched with the auth header as a blob and
// shown via an object URL (no public image URLs). Enterprise users get an add tile + per-photo
// delete; for everyone else this renders nothing unless photos already exist.
function AddressPhotos({ addressId, photoIds, canManage, onChanged }) {
  const intl = useIntl();
  const [urls, setUrls] = useState({});
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const ids = photoIds || [];

  useEffect(() => {
    let alive = true;
    const created = [];
    (async () => {
      const entries = await Promise.all(
        ids.map(async (id) => {
          try {
            const res = await axios.get(`${ROOT_API}apis/address-photo/${id}`, {
              headers: authHeaders(),
              responseType: "blob",
            });
            const url = URL.createObjectURL(res.data);
            created.push(url);
            return [id, url];
          } catch (e) {
            return null;
          }
        })
      );
      if (alive) setUrls(Object.fromEntries(entries.filter(Boolean)));
      else created.forEach((u) => URL.revokeObjectURL(u));
    })();
    return () => {
      alive = false;
      created.forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(ids)]);

  const upload = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("id", addressId);
    fd.append("file", file);
    setBusy(true);
    try {
      await axios.post(`${ROOT_API}apis/address-photo`, fd, { headers: authHeaders() });
      onChanged && (await onChanged());
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: intl.formatMessage({ id: "dashboard_photo_error" }),
      });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (photoId) => {
    setBusy(true);
    try {
      await axios.post(
        `${ROOT_API}apis/delete-address-photo`,
        { photo_id: photoId },
        { headers: authHeaders() }
      );
      onChanged && (await onChanged());
    } catch (err) {
      /* ignore */
    } finally {
      setBusy(false);
    }
  };

  if (!canManage && !ids.length) return null;

  return (
    <div className="dashboard__photos">
      {ids.map((id) => (
        <div className="dashboard__photo" key={id}>
          {urls[id] ? (
            <img src={urls[id]} alt="" loading="lazy" />
          ) : (
            <div className="dashboard__photo-ph" />
          )}
          {canManage ? (
            <button
              type="button"
              className="dashboard__photo-del"
              disabled={busy}
              onClick={() => remove(id)}
              aria-label={intl.formatMessage({ id: "dashboard_photo_delete" })}
            >
              ×
            </button>
          ) : null}
        </div>
      ))}
      {canManage ? (
        <>
          <button
            type="button"
            className="dashboard__photo-add"
            disabled={busy}
            title={intl.formatMessage({ id: "dashboard_photo_add" })}
            aria-label={intl.formatMessage({ id: "dashboard_photo_add" })}
            onClick={() => fileRef.current && fileRef.current.click()}
          >
            {busy ? "…" : "+"}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{ display: "none" }}
            onChange={upload}
          />
        </>
      ) : null}
    </div>
  );
}

// Local-time start of an outage = its date + the start of its "HH:MM - HH:MM" window
// (midnight if no parseable time). Returns ms, or null.
const outageStartMs = (o) => {
  const d = String(o.outage_date || o.date || "");
  if (d.length < 10) return null;
  const [y, mo, da] = d.slice(0, 10).split("-").map(Number);
  const m = (o.duration || "").match(/(\d{1,2})[:.](\d{2})/);
  return new Date(y, mo - 1, da, m ? +m[1] : 0, m ? +m[2] : 0).getTime();
};

// Soonest upcoming-outage start for an address (Infinity if none) — used to sort cubes.
const addressSoonest = (a) => {
  const starts = (a.upcoming_outages || []).map(outageStartMs).filter((x) => x != null);
  return starts.length ? Math.min(...starts) : Infinity;
};

const Home = () => {
  const intl = useIntl();
  const selectedLocale =
    useSelector((state) => state.lang.value) ||
    localStorage.getItem("lang") ||
    "hr";
  const locale = selectedLocale === "en" ? "en-US" : "hr-HR";
  const isPremium = isPremiumUser(useSelector((state) => state.user));
  const isAdmin =
    useSelector((state) => state.user?.role) === "admin";
  const userPlan = useSelector((state) => state.user?.plan);
  const isBusiness = isPremium && userPlan === "business";
  // Enterprise tier — gates the per-address photo upload/delete controls.
  const isEnterprise =
    isPremium && ["enterprise", "enterprise_concierge"].includes(userPlan);
  const [dashboard, setDashboard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [savingMethodId, setSavingMethodId] = useState(null);
  const [now, setNow] = useState(Date.now());

  // Tick once a second so the per-outage countdowns stay live.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const countdownLabel = (startMs) => {
    if (startMs == null) return "";
    const diff = startMs - now;
    if (diff <= 0) return intl.formatMessage({ id: "countdown_in_progress" });
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return intl.formatMessage({ id: "countdown_soon" });
    const d = Math.floor(mins / 1440);
    const h = Math.floor((mins % 1440) / 60);
    const m = mins % 60;
    const body = d > 0 ? `${d} d ${h} h` : h > 0 ? `${h} h ${m} min` : `${m} min`;
    return `${intl.formatMessage({ id: "countdown_prefix" })} ${body}`;
  };

  // Live HH:MM:SS (or "N d HH:MM:SS") for the prominent next-outage timer.
  const preciseCountdown = (startMs) => {
    const total = Math.max(0, Math.floor((startMs - now) / 1000));
    const pad = (n) => String(n).padStart(2, "0");
    const d = Math.floor(total / 86400);
    const hms = `${pad(Math.floor((total % 86400) / 3600))}:${pad(
      Math.floor((total % 3600) / 60)
    )}:${pad(total % 60)}`;
    return d > 0 ? `${d} d ${hms}` : hms;
  };

  // Small status lamp (LED) for an address row: red = outage affecting it now,
  // amber = an outage is scheduled, green = actively watching, nothing pending.
  const statusLamp = (address) => {
    const outages = address.upcoming_outages || [];
    // Ended outages are filtered out server-side, so a start in the past = in progress.
    const inProgress = outages.some((o) => {
      const ms = outageStartMs(o);
      return ms != null && ms <= now;
    });
    const state = inProgress ? "alert" : outages.length ? "soon" : "ok";
    const labelId =
      state === "alert"
        ? "dashboard_status_outage_found"
        : state === "soon"
        ? "dashboard_status_upcoming"
        : "dashboard_status_watching";
    const label = intl.formatMessage({ id: labelId });
    return (
      <span
        className={`dashboard__lamp dashboard__lamp--${state}`}
        role="status"
        aria-label={label}
        title={label}
      />
    );
  };

  const fetchDashboard = async () => {
    const res = await axios.get(`${ROOT_API}apis/user/dashboard`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
      },
    });
    return res?.data?.data || null;
  };

  // Refresh the dashboard in place (used after a photo upload/delete changes an address).
  const reload = async () => {
    try {
      setDashboard(await fetchDashboard());
    } catch (e) {
      /* keep current view */
    }
  };

  useEffect(() => {
    let alive = true;

    const loadDashboard = async () => {
      setIsLoading(true);
      setHasError(false);
      try {
        const data = await fetchDashboard();
        if (alive) {
          setDashboard(data);
        }
      } catch (error) {
        if (alive) {
          setHasError(true);
        }
      } finally {
        if (alive) {
          setIsLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      alive = false;
    };
  }, []);

  const handleDelete = async (address) => {
    if (deletingId) {
      return;
    }
    const confirmed = await Swal.fire({
      icon: "warning",
      title: intl.formatMessage({ id: "register_service_delete_confirm_title" }),
      text: intl.formatMessage({ id: "register_service_delete_confirm_text" }),
      showCancelButton: true,
      confirmButtonText: intl.formatMessage({ id: "register_service_delete_confirm_ok" }),
      cancelButtonText: intl.formatMessage({ id: "register_service_delete_cancel" }),
    });
    if (!confirmed.isConfirmed) {
      return;
    }
    try {
      setDeletingId(address.id);
      await axios.post(
        `${ROOT_API}apis/delete-outage-service`,
        { id: address.id },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      const data = await fetchDashboard();
      setDashboard(data);
      Swal.fire({
        icon: "success",
        title: intl.formatMessage({ id: "register_service_delete_success" }),
        confirmButtonText: "OK",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: intl.formatMessage({ id: "register_service_delete_error" }),
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleMethodChange = async (address, method) => {
    if (savingMethodId || address.notification_method === method) return;
    if ((method === "sms" || method === "both") && !isPremium) {
      Swal.fire({
        icon: "info",
        title: intl.formatMessage({ id: "account_channel_sms_premium" }),
        confirmButtonText: "OK",
      });
      return;
    }
    try {
      setSavingMethodId(address.id);
      await axios.post(
        `${ROOT_API}apis/update-outage-method`,
        { id: address.id, notification_method: method },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      setDashboard((prev) => ({
        ...prev,
        addresses: (prev.addresses || []).map((a) =>
          a.id === address.id ? { ...a, notification_method: method } : a
        ),
      }));
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: intl.formatMessage({ id: "there_are_error_occured" }),
      });
    } finally {
      setSavingMethodId(null);
    }
  };

  // Name/rename an address ("Kuća", "Ured"…). Paid-only — used as the label in alerts.
  const handleNickname = async (address) => {
    if (!isPremium || savingMethodId) return;
    const { value, isConfirmed } = await Swal.fire({
      title: intl.formatMessage({ id: "dashboard_nickname_title" }),
      input: "text",
      inputValue: address.nickname || "",
      inputPlaceholder: intl.formatMessage({ id: "dashboard_nickname_placeholder" }),
      inputAttributes: { maxlength: 60 },
      showCancelButton: true,
      confirmButtonText: intl.formatMessage({ id: "confirm" }),
      cancelButtonText: intl.formatMessage({ id: "register_service_delete_cancel" }),
    });
    if (!isConfirmed) return;
    const nickname = (value || "").trim();
    try {
      setSavingMethodId(address.id);
      await axios.post(
        `${ROOT_API}apis/update-outage-method`,
        { id: address.id, nickname },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      setDashboard((prev) => ({
        ...prev,
        addresses: (prev.addresses || []).map((a) =>
          a.id === address.id ? { ...a, nickname: nickname || null } : a
        ),
      }));
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: intl.formatMessage({ id: "there_are_error_occured" }),
      });
    } finally {
      setSavingMethodId(null);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="dashboard">
          <div className="container">
            <div className="dashboard__loading">
              {intl.formatMessage({ id: "dashboard_loading" })}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (hasError || !dashboard) {
    return (
      <Layout>
        <div className="dashboard">
          <div className="container">
            <div className="dashboard__error">
              <img
                className="dashboard__error-art"
                src={`${process.env.PUBLIC_URL}/assets/mascot/beaver-confused.png`}
                alt=""
                aria-hidden="true"
              />
              <p>{intl.formatMessage({ id: "dashboard_error" })}</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const { summary, addresses, user } = dashboard;
  // Soonest cutoff first, so the address about to lose power/water leads. (Plain const, not a
  // hook — this runs after the early returns above.)
  const sortedAddresses = [...(addresses || [])].sort(
    (a, b) => addressSoonest(a) - addressSoonest(b)
  );
  // Soonest UPCOMING (future-start) outage across all addresses → the prominent countdown.
  const nextOutage =
    (addresses || [])
      .flatMap((a) =>
        (a.upcoming_outages || []).map((o) => ({
          addressLabel: a.label,
          service: o.service,
          date: o.outage_date || o.date,
          duration: o.duration,
          startMs: outageStartMs(o),
        }))
      )
      .filter((o) => o.startMs != null && o.startMs > now)
      .sort((a, b) => a.startMs - b.startMs)[0] || null;
  const displayName = user?.full_name || intl.formatMessage({ id: "my_account" });

  return (
    <Layout>
      <div className="dashboard">
        <div className="container">
          <section className="dashboard__hero">
            <div>
              <p className="dashboard__eyebrow">
                {intl.formatMessage({ id: "dashboard_eyebrow" })}
              </p>
              <h1 className="dashboard__title">
                {intl.formatMessage(
                  { id: "dashboard_title" },
                  { name: displayName }
                )}
              </h1>
              <p className="dashboard__lead">
                {intl.formatMessage({ id: "dashboard_subtitle" })}
              </p>
            </div>
            <div className="dashboard__hero-actions">
              <Link to="/register-service" className="btn-cta">
                {intl.formatMessage({ id: "dashboard_add_address" })}
              </Link>
              <Link to="/danas" className="dashboard__hero-premium">
                {intl.formatMessage({ id: "dashboard_view_today" })}
              </Link>
              {!isPremium ? (
                <Link to="/pretplata" className="dashboard__hero-premium">
                  {intl.formatMessage({ id: "dashboard_go_premium" })}
                </Link>
              ) : isBusiness ? (
                <span className="dashboard__premium-badge">
                  {intl.formatMessage({ id: "dashboard_business_badge" })}
                </span>
              ) : (
                <Link
                  to="/pretplata"
                  className="dashboard__premium-badge dashboard__premium-badge--link"
                  title={intl.formatMessage({ id: "account_sub_upgrade" })}
                >
                  {intl.formatMessage({ id: "dashboard_premium_manage" })}
                </Link>
              )}
              {isAdmin ? (
                <Link to="/admin" className="dashboard__hero-admin">
                  {intl.formatMessage({ id: "dashboard_admin_panel" })}
                </Link>
              ) : null}
            </div>
          </section>

          {nextOutage ? (
            <section className="dashboard__countdown">
              <div className="dashboard__countdown-head">
                <span
                  className={`dashboard__svc dashboard__svc--${
                    nextOutage.service === "water" ? "water" : "electricity"
                  }`}
                >
                  {nextOutage.service === "water" ? "💧" : "⚡"}{" "}
                  {intl.formatMessage({
                    id:
                      nextOutage.service === "water"
                        ? "service_tag_water"
                        : "service_tag_electricity",
                  })}
                </span>
                <span className="dashboard__countdown-title">
                  {intl.formatMessage({ id: "dashboard_next_outage" })}
                </span>
              </div>
              <div className="dashboard__countdown-timer">
                {preciseCountdown(nextOutage.startMs)}
              </div>
              <div className="dashboard__countdown-meta">
                {[
                  nextOutage.addressLabel,
                  formatDate(nextOutage.date, locale),
                  nextOutage.duration,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </section>
          ) : null}

          <section className="dashboard__summary">
            <article className="dashboard__summary-card">
              <span className="dashboard__summary-label">
                {intl.formatMessage({ id: "dashboard_summary_addresses" })}
              </span>
              <strong className="dashboard__summary-value">
                {summary?.watched_addresses ?? 0}
              </strong>
            </article>
            <article className="dashboard__summary-card">
              <span className="dashboard__summary-label">
                {intl.formatMessage({ id: "dashboard_summary_outages" })}
              </span>
              <strong className="dashboard__summary-value">
                {summary?.upcoming_outages ?? 0}
              </strong>
            </article>
            <article className="dashboard__summary-card">
              <span className="dashboard__summary-label">
                {intl.formatMessage({ id: "dashboard_summary_last_check" })}
              </span>
              <strong className="dashboard__summary-value">
                {summary?.last_crawl_at
                  ? formatDateTime(summary.last_crawl_at, locale)
                  : intl.formatMessage({ id: "dashboard_no_check_yet" })}
              </strong>
            </article>
          </section>

          {!isPremium ? (
            <section className="dashboard__freebar">
              <span>{intl.formatMessage({ id: "dashboard_free_leadtime" })}</span>
              <Link to="/pretplata" className="dashboard__freebar-cta">
                {intl.formatMessage({ id: "dashboard_free_leadtime_cta" })}
              </Link>
            </section>
          ) : null}

          <section className="dashboard__grid">
            <div className="dashboard__panel">
              <h2 className="dashboard__panel-title">
                {intl.formatMessage({ id: "dashboard_addresses_title" })}
              </h2>
              <p className="dashboard__panel-copy">
                {intl.formatMessage({ id: "dashboard_addresses_copy" })}
              </p>

              {addresses?.length ? (
                <div className="dashboard__cubes">
                  {sortedAddresses.map((address) => (
                    <article className="dashboard__cube" key={address.id}>
                      <div className="dashboard__address-top">
                        <div>
                          <h3 className="dashboard__address-label">
                            {address.nickname || address.label}
                            {isPremium ? (
                              <button
                                type="button"
                                className="dashboard__nick-edit"
                                title={intl.formatMessage({ id: "dashboard_nickname_edit" })}
                                aria-label={intl.formatMessage({ id: "dashboard_nickname_edit" })}
                                onClick={() => handleNickname(address)}
                              >
                                ✎
                              </button>
                            ) : null}
                          </h3>
                          <p className="dashboard__address-sub">
                            {address.nickname ? `${address.label} · ` : ""}
                            {intl.formatMessage({ id: "dashboard_watching_since" })}{" "}
                            {formatDate(address.created_at, locale)}
                          </p>
                        </div>
                        {statusLamp(address)}
                      </div>

                      <AddressPhotos
                        addressId={address.id}
                        photoIds={address.photos}
                        canManage={isEnterprise}
                        onChanged={reload}
                      />

                      {address.upcoming_outages?.length ? (
                        <div className="dashboard__outages">
                          {[...address.upcoming_outages]
                            .sort((a, b) => (outageStartMs(a) ?? 0) - (outageStartMs(b) ?? 0))
                            .map((outage) => (
                              <div className="dashboard__outage" key={outage.id}>
                                <div className="dashboard__outage-head">
                                  <span
                                    className={`dashboard__svc dashboard__svc--${
                                      outage.service === "water" ? "water" : "electricity"
                                    }`}
                                  >
                                    {outage.service === "water" ? "💧" : "⚡"}{" "}
                                    {intl.formatMessage({
                                      id:
                                        outage.service === "water"
                                          ? "service_tag_water"
                                          : "service_tag_electricity",
                                    })}
                                  </span>
                                  <span className="dashboard__countdown">
                                    {countdownLabel(outageStartMs(outage))}
                                  </span>
                                </div>
                                <strong>
                                  {formatDate(outage.outage_date, locale)}
                                  {outage.duration ? ` · ${outage.duration}` : ""}
                                </strong>
                                <p>{outage.street_raw || outage.street || outage.location}</p>
                              </div>
                            ))}
                        </div>
                      ) : (
                        <div className="dashboard__empty dashboard__empty--calm">
                          <img
                            className="dashboard__calm-art"
                            src={`${process.env.PUBLIC_URL}/assets/mascot/beaver-cozy-trim.png`}
                            alt=""
                            aria-hidden="true"
                          />
                          <strong>
                            {intl.formatMessage({ id: "dashboard_no_matches_title" })}
                          </strong>
                          <p>{intl.formatMessage({ id: "dashboard_no_matches_copy" })}</p>
                        </div>
                      )}

                      <div className="dashboard__cube-foot">
                        <div className="dashboard__channel" role="group">
                          {CHANNELS.map((c) => (
                            <button
                              key={c}
                              type="button"
                              className={`dashboard__channel-opt${
                                address.notification_method === c ? " is-active" : ""
                              }`}
                              disabled={savingMethodId === address.id}
                              onClick={() => handleMethodChange(address, c)}
                            >
                              {intl.formatMessage({ id: `account_channel_${c}` })}
                            </button>
                          ))}
                        </div>
                        <button
                          type="button"
                          className="dashboard__address-delete"
                          onClick={() => handleDelete(address)}
                          disabled={deletingId === address.id}
                        >
                          {intl.formatMessage({
                            id:
                              deletingId === address.id
                                ? "register_service_submitting"
                                : "register_service_delete",
                          })}
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="dashboard__empty">
                  <img
                    className="dashboard__empty-art"
                    src={`${process.env.PUBLIC_URL}/assets/mascot/beaver-wave.png`}
                    alt=""
                    aria-hidden="true"
                  />
                  <strong>
                    {intl.formatMessage({ id: "dashboard_no_addresses_title" })}
                  </strong>
                  <p>{intl.formatMessage({ id: "dashboard_no_addresses_body" })}</p>
                  <Link to="/register-service" className="btn-cta">
                    {intl.formatMessage({ id: "dashboard_open_add_address" })}
                  </Link>
                </div>
              )}
            </div>

          </section>

          <details className="dashboard__guide">
            <summary className="dashboard__guide-summary">
              {intl.formatMessage({ id: "dashboard_guide_summary" })}
            </summary>
            <div className="dashboard__guide-body">
              <p>{intl.formatMessage({ id: "dashboard_trust_copy" })}</p>
              <ul className="dashboard__trust-list">
                <li>{intl.formatMessage({ id: "dashboard_trust_item_source" })}</li>
                <li>{intl.formatMessage({ id: "dashboard_trust_item_scope" })}</li>
                <li>{intl.formatMessage({ id: "dashboard_trust_item_updates" })}</li>
              </ul>
            </div>
          </details>
        </div>
      </div>
    </Layout>
  );
};

export default Home;
